// ---------------------------------------------------------------------------
// EditorToolbar — Top toolbar with undo/redo, device selector, zoom, and
// preview toggle.
//
// Insert is handled by the LeftPanel tab, not the toolbar.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useUndoStateStore } from '@/mcp/undo-state-store'
import {
  Undo2,
  Redo2,
  Eye,
  Pencil,
  ZoomIn,
  ZoomOut,
  Check,
  Keyboard,
  Download,
  Upload,
  MousePointer,
  LayoutGrid,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { SHORTCUT } from '@/lib/shortcuts'
import { downloadProjectJson, openImportDialog } from '@/features/page-editor/lib/export'
import { toast } from '@/store/toast-store'
import { DeviceToolbar } from './device-toolbar'
import type { LucideIcon } from 'lucide-react'

const ZOOM_PRESETS = [
  { label: '50%', value: 0.5 },
  { label: '75%', value: 0.75 },
  { label: '100%', value: 1 },
  { label: '125%', value: 1.25 },
  { label: '150%', value: 1.5 },
  { label: '200%', value: 2 },
]

// ---------------------------------------------------------------------------
// ToolbarButton — reusable icon button for the toolbar
// ---------------------------------------------------------------------------

interface ToolbarButtonProps {
  icon: LucideIcon
  title: string
  disabled?: boolean
  active?: boolean
  onClick: () => void
}

function ToolbarButton({ icon: Icon, title, disabled, active, onClick }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          disabled={disabled}
          title={title}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-xl transition-colors',
            'text-muted-foreground hover:bg-accent hover:text-foreground',
            'disabled:pointer-events-none disabled:opacity-40',
            active && 'bg-accent text-foreground',
          )}
        >
          <Icon size={16} />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{title}</p>
      </TooltipContent>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// ZoomDropdown — click on zoom % to pick a preset level
// ---------------------------------------------------------------------------

function ZoomDropdown({ zoom, setZoom }: { zoom: number; setZoom: (z: number) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const currentPct = Math.round(zoom * 100)

  return (
    <div className="relative" ref={ref}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="w-12 text-center text-xs text-muted-foreground rounded-xl py-0.5 hover:bg-accent hover:text-foreground transition-colors"
          >
            {currentPct}%
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Zoom Level</p>
        </TooltipContent>
      </Tooltip>

      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 w-28 rounded-xl border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95">
          {ZOOM_PRESETS.map((preset) => {
            const isActive = currentPct === Math.round(preset.value * 100)
            return (
              <button
                key={preset.label}
                type="button"
                className={cn(
                  'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs transition-colors',
                  isActive ? 'bg-accent font-medium' : 'hover:bg-accent',
                )}
                onClick={() => {
                  setZoom(preset.value)
                  setOpen(false)
                }}
              >
                {preset.label}
                {isActive && <Check className="h-3 w-3" />}
              </button>
            )
          })}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent transition-colors"
            onClick={() => {
              setZoom(1)
              setOpen(false)
            }}
          >
            Reset to 100%
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.zoomTo100}</span>
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// EditorToolbar
// ---------------------------------------------------------------------------

interface EditorToolbarProps {
  onOpenShortcuts?: () => void
  title?: string
}

export function EditorToolbar({ onOpenShortcuts, title = 'Page' }: EditorToolbarProps) {
  const undo = useEditorStore((s) => s.undo)
  const redo = useEditorStore((s) => s.redo)
  const canUndo = useUndoStateStore((s) => s.canUndo)
  const canRedo = useUndoStateStore((s) => s.canRedo)
  const isPreview = useEditorStore((s) => s.isPreview)
  const togglePreview = useEditorStore((s) => s.togglePreview)
  const zoom = useEditorStore((s) => s.zoom)
  const setZoom = useEditorStore((s) => s.setZoom)

  const pageLayout = useEditorStore((s) => {
    const page = s.app?.pages.find((p) => p.id === s.activePageId)
    return page?.layout ?? 'stack'
  })

  const toggleLayout = () => {
    const { activePageId } = useEditorStore.getState()
    if (!activePageId) return
    const newLayout = pageLayout === 'absolute' ? 'flow' : 'absolute'
    useEditorStore.getState().setPageLayout(activePageId, newLayout)
  }

  const zoomIn = () => setZoom(Math.min(zoom + 0.1, 3))
  const zoomOut = () => setZoom(Math.max(zoom - 0.1, 0.25))

  const handleExport = () => {
    const state = useEditorStore.getState()
    downloadProjectJson({
      app: state.app,
      blocks: state.blocks,
      appTarget: state.appTarget,
      data: state.data,
      flows: state.flows,
      tokens: state.tokens,
    })
    toast.success('Project exported')
  }

  const handleImport = async () => {
    try {
      const loaded = await openImportDialog()
      if (loaded) {
        useEditorStore.getState().loadApp(loaded)
        toast.success('Project imported')
      }
    } catch (err) {
      console.error('[Import] Failed:', err)
      toast.error(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div data-testid="editor-toolbar" className="flex h-10 items-center justify-between border-b bg-background px-3">
        {/* Left section — branding + undo/redo */}
        <div className="flex items-center gap-1">
          <span className="mr-3 text-sm font-semibold">{title}</span>
          <ToolbarButton icon={Undo2} disabled={!canUndo} onClick={undo} title={`Undo (${SHORTCUT.undo})`} />
          <ToolbarButton icon={Redo2} disabled={!canRedo} onClick={redo} title={`Redo (${SHORTCUT.redo})`} />
        </div>

        {/* Center section — device breakpoint selector + layout toggle */}
        <div className="flex items-center gap-1">
          <DeviceToolbar />
          <div className="mx-1 h-4 w-px bg-border" />
          <ToolbarButton
            icon={pageLayout === 'absolute' ? MousePointer : LayoutGrid}
            onClick={toggleLayout}
            title={pageLayout === 'absolute' ? 'Switch to Flow Layout' : 'Switch to Absolute Layout'}
            active={pageLayout === 'absolute'}
          />
        </div>

        {/* Right section — zoom + preview */}
        <div className="flex items-center gap-1">
          <ToolbarButton icon={ZoomOut} onClick={zoomOut} title={`Zoom Out (${SHORTCUT.zoomOut})`} />
          <ZoomDropdown zoom={zoom} setZoom={setZoom} />
          <ToolbarButton icon={ZoomIn} onClick={zoomIn} title={`Zoom In (${SHORTCUT.zoomIn})`} />
          <div className="mx-1 h-4 w-px bg-border" />
          <ToolbarButton icon={Upload} onClick={handleImport} title={`Import JSON (${SHORTCUT.open})`} />
          <ToolbarButton icon={Download} onClick={handleExport} title={`Export JSON (${SHORTCUT.save})`} />
          <ToolbarButton
            icon={isPreview ? Pencil : Eye}
            onClick={togglePreview}
            title={isPreview ? `Edit Mode (${SHORTCUT.preview})` : `Preview (${SHORTCUT.preview})`}
            active={isPreview}
          />
          {onOpenShortcuts && (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              <ToolbarButton icon={Keyboard} onClick={onOpenShortcuts} title="Keyboard Shortcuts (?)" />
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
